// App.tsx
// Main application with scheduling
import 'react-quill/dist/quill.snow.css';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AlertProvider } from './contexts/AlertContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';
import { VideoUpload } from './components/VideoUpload';
import { AudioUpload } from './components/AudioUpload';
import { ArticleCreate } from './components/ArticleCreate';
import { ContentLibrary } from './components/ContentLibrary';
import { VideoEdit } from './components/VideoEdit';
import { AudioEdit } from './components/AudioEdit';
import { ArticleEdit } from './components/ArticleEdit';
import { Drafts } from './components/Drafts';
import { ScheduledPosts } from './components/ScheduledPosts';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';
import { ModerationQueue } from './components/ModerationQueue';
import { PodcastModerationQueue } from './components/PodcastModerationQueue';
import { QuizLibrary } from './components/QuizLibrary';
import { QuizDetails } from './components/QuizDetails';
import { QuizAnalytics } from './components/QuizAnalytics';
import { MessageInbox } from './components/AdminMessaging/MessageInbox';
import { MessageDetail } from './components/AdminMessaging/MessageDetail';
import { MessageTemplates } from './components/AdminMessaging/MessageTemplates';
import { MessagingAnalytics } from './components/AdminMessaging/MessagingAnalytics';
import { ProactiveMessage } from './components/AdminMessaging/ProactiveMessage';
import { AIDashboard } from './components/ai/AIDashboard';
import { AIUsageAnalytics } from './components/ai/AIUsageAnalytics';
import { OmoreAnalytics } from './components/omore/OmoreAnalytics';
import { OmoreModerationQueue } from './components/omore/OmoreModerationQueue';
import { OmoreUserAbuse } from './components/omore/OmoreUserAbuse';
import { OmoreRecentSessions } from './components/omore/OmoreRecentSessions';
import { OmoreSessionDetail } from './components/omore/OmoreSessionDetail';
import { OmoreTrends } from './components/omore/OmoreTrends';
import { OmoreCohortAnalysis } from './components/omore/OmoreCohortAnalysis';
import { AlertSettings } from './components/alerts/AlertSettings';
import { BizSpotlight } from './components/BizSpotlight/BizSpotlight';
import { BizBusinessPlans } from './components/BizBusinessPlans/BizBusinessPlans';
import { BizMessages } from './components/BizMessages/BizMessages';
import { BizMessageDetail } from './components/BizMessages/BizMessageDetail';
import { AdminRoles } from './components/AdminRoles/AdminRoles';
import { UserDirectory } from './components/UserDirectory/UserDirectory';
import './App.css';


/**
 * The Ask Omore pages used to live under /koko. Admins may still have those URLs
 * bookmarked, so rewrite the leading segment and forward them to /omore.
 * Only the first segment changes, which keeps route params (:sessionId), query
 * strings and hashes intact.
 */
const LegacyKokoRedirect: React.FC = () => {
    const { pathname, search, hash } = useLocation();
    return <Navigate to={`${pathname.replace(/^\/koko/, '/omore')}${search}${hash}`} replace />;
};

function App() {
    return (
        <ErrorBoundary>
            <Router>
                <AuthProvider>
                    <AlertProvider>
                    <Routes>
                    <Route path="/login" element={<Login />} />

                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/content-library"
                        element={
                            <ProtectedRoute>
                                <ContentLibrary />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/scheduled-posts"
                        element={
                            <ProtectedRoute>
                                <ScheduledPosts />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/drafts"
                        element={
                            <ProtectedRoute>
                                <Drafts />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/upload-video"
                        element={
                            <ProtectedRoute>
                                <VideoUpload />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/upload-audio"
                        element={
                            <ProtectedRoute>
                                <AudioUpload />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/create-article"
                        element={
                            <ProtectedRoute>
                                <ArticleCreate />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/edit-video/:postId"
                        element={
                            <ProtectedRoute>
                                <VideoEdit />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/edit-audio/:postId"
                        element={
                            <ProtectedRoute>
                                <AudioEdit />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/edit-article/:postId"
                        element={
                            <ProtectedRoute>
                                <ArticleEdit />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/analytics"
                        element={
                            <ProtectedRoute>
                                <AnalyticsDashboard />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/moderation"
                        element={
                            <ProtectedRoute>
                                <ModerationQueue />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/podcast-moderation"
                        element={
                            <ProtectedRoute>
                                <PodcastModerationQueue />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/quiz-creator"
                        element={
                            <ProtectedRoute>
                                <QuizLibrary />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/quiz-creator/quiz/:quizId"
                        element={
                            <ProtectedRoute>
                                <QuizDetails />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/quiz-creator/analytics/:quizId"
                        element={
                            <ProtectedRoute>
                                <QuizAnalytics />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin-messaging"
                        element={
                            <ProtectedRoute>
                                <MessageInbox />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin-messaging/message/:messageId"
                        element={
                            <ProtectedRoute>
                                <MessageDetail />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin-messaging/templates"
                        element={
                            <ProtectedRoute>
                                <MessageTemplates />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin-messaging/analytics"
                        element={
                            <ProtectedRoute>
                                <MessagingAnalytics />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin-messaging/new"
                        element={
                            <ProtectedRoute>
                                <ProactiveMessage />
                            </ProtectedRoute>
                        }
                    />

                    {/* AI & Analytics */}
                    <Route
                        path="/ai"
                        element={
                            <ProtectedRoute>
                                <AIDashboard />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/ai/usage"
                        element={
                            <ProtectedRoute>
                                <AIUsageAnalytics />
                            </ProtectedRoute>
                        }
                    />

                    {/* Ask Omore */}
                    <Route
                        path="/omore"
                        element={
                            <ProtectedRoute>
                                <OmoreAnalytics />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/omore/moderation"
                        element={
                            <ProtectedRoute>
                                <OmoreModerationQueue />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/omore/users"
                        element={
                            <ProtectedRoute>
                                <OmoreUserAbuse />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/omore/sessions"
                        element={
                            <ProtectedRoute>
                                <OmoreRecentSessions />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/omore/sessions/:sessionId"
                        element={
                            <ProtectedRoute>
                                <OmoreSessionDetail />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/omore/trends"
                        element={
                            <ProtectedRoute>
                                <OmoreTrends />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/omore/cohorts"
                        element={
                            <ProtectedRoute>
                                <OmoreCohortAnalysis />
                            </ProtectedRoute>
                        }
                    />

                    {/* Alerts */}
                    <Route
                        path="/alerts/settings"
                        element={
                            <ProtectedRoute>
                                <AlertSettings />
                            </ProtectedRoute>
                        }
                    />

                    {/* Biz Admin — biz.omore.app */}
                    <Route
                        path="/biz-spotlight"
                        element={<ProtectedRoute><BizSpotlight /></ProtectedRoute>}
                    />
                    <Route
                        path="/biz-plans"
                        element={<ProtectedRoute><BizBusinessPlans /></ProtectedRoute>}
                    />
                    <Route
                        path="/biz-messages"
                        element={<ProtectedRoute><BizMessages /></ProtectedRoute>}
                    />
                    <Route
                        path="/biz-messages/:userId"
                        element={<ProtectedRoute><BizMessageDetail /></ProtectedRoute>}
                    />

                    {/* Admin role management */}
                    <Route
                        path="/admin-roles"
                        element={<ProtectedRoute><AdminRoles /></ProtectedRoute>}
                    />

                    {/* User directory (Phase 4 flat profiles index) */}
                    <Route
                        path="/users"
                        element={<ProtectedRoute><UserDirectory /></ProtectedRoute>}
                    />

                    {/* Legacy Ask Omore URLs — /koko/* redirects to /omore/* */}
                    <Route path="/koko" element={<LegacyKokoRedirect />} />
                    <Route path="/koko/*" element={<LegacyKokoRedirect />} />

                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
                    </AlertProvider>
                </AuthProvider>
            </Router>
        </ErrorBoundary>
    );
}

export default App;
